import React, { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  BundleOrRecycling,
  LaborTime,
  Reference,
} from '@1po/1po-bff-fe-spec/generated/estimate/response/model/Estimate';
import { Document, Image, Page, View } from '@react-pdf/renderer';
import moment from 'moment';
import { EprelPicture } from 'components/Pdf/EprelPicture';
import { getBrandLogo } from 'components/Pdf/PdfSvg';
import {
  buildUniformColumns,
  Divider,
  PageNumber,
  PDF_DASH,
  PdfHeaderBloc,
  PdfTwoPanelBlock,
  Section,
  Spacer,
  Subtitle,
  Table,
  Text,
  TextBold,
  TextDark,
  VerticalSpacer,
} from 'components/Pdf/PdfUI';
import { components, fonts, styles } from 'components/Pdf/PdfUI.styles';
import { formatAddress, getFirstValidAddress } from 'domains/garage/Garage.utils';
import { PdfFilterProps } from 'pages/EstimatePage/EstimateModifyTab';
import { EstimatePdfBrand, EstimatePdfData } from 'pages/EstimatePdf/EstimatePdfPage';
import { theme } from 'styles';
import { textFormatter } from 'utils/format';
import { formatVehicleBrand } from 'utils/mappers';

const cutLastSlash = (designationValue: string) =>
  designationValue.includes('/') ? designationValue.substring(0, designationValue.lastIndexOf('/')) : designationValue;

// Strips the reference number (or a trailing "/ …" segment) from a designation when reference numbers are hidden.
const removeReferenceFromDesignation = (designationValue: string, referenceNumber: string) => {
  const escapedReferenceNumber = designationValue.includes(referenceNumber)
    ? designationValue.replace(referenceNumber, '')
    : cutLastSlash(designationValue);

  const trimmedReferenceNumber = escapedReferenceNumber.trim();
  return trimmedReferenceNumber.endsWith('/') ? trimmedReferenceNumber.slice(0, -1).trim() : trimmedReferenceNumber;
};

const EstimateInfoPanel = ({ data, width }: { data: EstimatePdfData; width: number }) => {
  const estimateValidityUnit = data.validityUnit === 'WEEK' ? 'week' : 'month';
  const date = textFormatter.formatDate(data.date);
  const validityDate = data.estimateValidFor
    ? textFormatter.formatDate(moment(data.date).add(data.estimateValidFor, estimateValidityUnit).toDate())
    : '';
  return (
    <View style={[components.border_grey, { width }]} wrap={false}>
      <View style={[components.card_title_center, { height: '23px' }]}>
        <TextBold>
          {data.text.title} {data.sequenceNumber ?? ''}
        </TextBold>
      </View>
      <View style={[styles.col, { width: '100%' }]}>
        <View style={[styles.flex]}>
          <View style={[styles.row, { marginLeft: 7, marginRight: 7, marginTop: 7, marginBottom: 7 }]}>
            <Text>
              <Trans i18nKey={'estimate.pdf.date'} tOptions={{ date }}>
                Date: {{ date }}
              </Trans>
            </Text>
          </View>
          <Divider />
          <View style={[styles.row, { marginLeft: 7, marginRight: 7, marginTop: 7, marginBottom: 7 }]}>
            <Text>
              {' '}
              <Trans i18nKey={'estimate.pdf.commercial_validity'} tOptions={{ validityDate }}>
                Commercial estimate validity: {{ validityDate }}
              </Trans>
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const VehicleCardImpl = ({
  cardTitle,
  title,
  modelType,
  engine,
  gearbox,
  vin,
  vrn,
  manufacturingDate,
  mileage,
}: {
  cardTitle: string | ReactNode;
  title: string;
  gearbox: string | undefined;
  modelType: string | undefined;
  engine: string | undefined;
  vin: string | undefined;
  vrn: string | undefined;
  manufacturingDate: string | undefined;
  mileage: string | undefined;
}) => {
  const { t } = useTranslation();
  return (
    <View style={[{ width: '100%' }]}>
      <View style={[components.card_title_center, { height: '23px' }]}>
        <TextBold>{cardTitle}</TextBold>
      </View>
      <View style={[styles.col, { width: '100%' }]}>
        <View style={styles.row}>
          <View
            style={[
              styles.column,
              {
                marginLeft: 7,
                marginTop: 5,
                width: 164,
                justifyContent: 'space-between',
              },
            ]}
          >
            <View style={styles.flex}>
              <View key={'content-title'} style={{ width: 164, height: 11 }}>
                <Section>{title}</Section>
              </View>
              {!!modelType && (
                <View key={'content-model-type'} style={{ width: 164, height: 11 }}>
                  <Text>{modelType}</Text>
                </View>
              )}
              {!!engine && (
                <View key={'content-engine'} style={{ width: 164, height: 11 }}>
                  <Text>{engine}</Text>
                </View>
              )}
              {!!gearbox && (
                <View key={'content-gearbox'} style={{ width: 164, height: 22 }}>
                  <Text>{gearbox}</Text>
                </View>
              )}
            </View>
          </View>
          <View
            style={[
              styles.column,
              {
                marginLeft: 8,
                marginRight: 7,
                marginTop: 5,
                marginBottom: 5,
                width: 164,
              },
            ]}
          >
            <View style={styles.flex}>
              <View key={'input-0'}>
                <Text>{t('estimate.pdf.vin', 'VIN : {{ vin }}', { vin })}</Text>
              </View>
              <View key={'input-1'}>
                <Text>{t('estimate.pdf.vrn', 'VRN : {{ vrn }}', { vrn })}</Text>
              </View>
              <View key={'input-second-0'}>
                <Text>
                  {t('estimate.pdf.manufacturing_date', '1st Circulation date: {{ manufacturingDate }}', {
                    manufacturingDate,
                  })}
                </Text>
              </View>
              {/* <View style={{ marginTop: 5 }} key={'input-second-1'}> */}
              <View key={'input-second-1'}>
                <Text>
                  {t('estimate.pdf.mileage', 'Mileage: {{ mileage }}', {
                    mileage,
                  })}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const IAMVehicleCard = ({ data }: { data: EstimatePdfData }) => {
  const { t } = useTranslation();
  if (!data.vehicleDetail) return null;
  const { vehicleBrand, iamVehicle, vin, vrn, model } = data.vehicleDetail;
  if (!iamVehicle) return null;
  const title = (formatVehicleBrand(vehicleBrand) ?? '') + (model ? ` ${model}` : '');
  const vehicle = data.vehicleDetail;
  const manufacturingDate = vehicle?.manufacturingDate;
  const mileage = vehicle?.mileage;
  return (
    <VehicleCardImpl
      cardTitle={<Trans i18nKey="estimate.pdf.vehicle_information">Vehicle information</Trans>}
      title={title}
      gearbox={undefined}
      engine={`${iamVehicle.engineCodes} ${iamVehicle.energy}`}
      modelType={`${iamVehicle.bodyType} ${
        iamVehicle.doors ? iamVehicle.doors + t('catalog.vehicle.doors', ' Doors') : ''
      }`}
      manufacturingDate={manufacturingDate}
      mileage={mileage}
      vin={vin}
      vrn={vrn}
    />
  );
};

const DataHubVehicleCard = ({ data }: { data: EstimatePdfData }) => {
  if (!data.vehicleDetail) return null;
  const { dataHubVehicle } = data.vehicleDetail;
  if (!dataHubVehicle) return null;
  const { name, engine, modelType, gearbox } = dataHubVehicle;
  const vehicle = data.vehicleDetail;
  const manufacturingDate = vehicle?.manufacturingDate;
  const mileage = vehicle?.mileage;
  const vin = vehicle?.vin;
  const vrn = vehicle?.vrn;

  return (
    <VehicleCardImpl
      cardTitle={<Trans i18nKey="estimate.pdf.vehicle_information">Vehicle information</Trans>}
      title={name ? `${name}` : ''}
      engine={engine}
      modelType={modelType}
      gearbox={gearbox}
      vin={vin}
      vrn={vrn}
      manufacturingDate={manufacturingDate}
      mileage={mileage}
    />
  );
};

const VehicleCard = ({ data, width }: { data: EstimatePdfData; width: number }) => {
  if (!data.vehicleDetail) return null;
  return (
    <View style={[components.border_grey, { width }]}>
      {data.vehicleDetail.dataHubVehicle && <DataHubVehicleCard data={data} />}
      {data.vehicleDetail.dataHubVehicle === undefined && data.vehicleDetail.iamVehicle && (
        <IAMVehicleCard data={data} />
      )}
    </View>
  );
};

const ClientContactCard = ({ data, isB2B }: { data: EstimatePdfData; isB2B: boolean }) => {
  const client = data.client;
  const name = client?.name;
  const phone = client?.phoneNumber;
  const email = client?.email;
  const rrfCode = client?.rrfCode;
  const street = client?.street;
  const postalCode = client?.postalCode;
  const city = client?.city;
  const postalAddress = [street, postalCode, city].filter(Boolean).join(', ');

  const getTitle = () => {
    if (isB2B) {
      return <Trans i18nKey="estimate.pdf.garage_contact">Garage contact</Trans>;
    }
    return <Trans i18nKey="estimate.pdf.client_contact">Client contact</Trans>;
  };

  const title = getTitle();
  return (
    <View style={[styles.col_end]}>
      <View style={styles.line}>
        <TextBold>{title}</TextBold>
      </View>
      {Boolean(name) && (
        <View style={styles.line}>
          <Text>{name}</Text>
        </View>
      )}
      {isB2B && Boolean(rrfCode) && (
        <View style={styles.line}>
          <Text>{rrfCode}</Text>
        </View>
      )}
      {Boolean(phone) && (
        <View style={styles.line}>
          <Text>{phone}</Text>
        </View>
      )}
      {Boolean(email) && (
        <View style={styles.line}>
          <Text>{email}</Text>
        </View>
      )}
      {!isB2B && Boolean(postalAddress) && (
        <View style={styles.line}>
          <Text>{postalAddress}</Text>
        </View>
      )}
    </View>
  );
};

const GarageCard = ({ data }: { data: EstimatePdfData }) => {
  const garage = data.dealer;
  const garageName = garage?.name ?? '';
  const address = garage ? getFirstValidAddress(garage) : undefined;
  const institutionNumber = garage?.registrationNumber ?? '';
  const intraCommunityVat = data.intraCommunityVat ?? '';
  const phone = address?.phone ?? '';
  const email = address?.email ?? '';

  const street = formatAddress(address);
  const postalCode = address?.zipCode;
  const city = address?.city;
  const postalAddress = [street, postalCode, city].filter(Boolean).join(', ');

  return (
    <View style={[styles.col]}>
      {Boolean(garageName) && (
        <View style={styles.line}>
          <TextBold>{garageName}</TextBold>
        </View>
      )}
      {Boolean(institutionNumber) && (
        <View style={styles.line}>
          <Text>
            <Trans i18nKey="estimate.pdf.institution_number">Institution number : {{ institutionNumber }}</Trans>
          </Text>
        </View>
      )}
      {Boolean(intraCommunityVat) && (
        <View style={styles.line}>
          <Text>
            <Trans i18nKey="estimate.pdf.intra_community_vat">VAT intra: {{ intraCommunityVat }}</Trans>
          </Text>
        </View>
      )}
      {Boolean(phone) && (
        <View style={styles.line}>
          <Text>{phone}</Text>
        </View>
      )}
      {Boolean(email) && (
        <View style={styles.line}>
          <Text>{email}</Text>
        </View>
      )}
      {Boolean(postalAddress) && (
        <View style={styles.line}>
          <Text>{postalAddress}</Text>
        </View>
      )}
    </View>
  );
};

const R1GarageCard = ({ data }: { data: EstimatePdfData }) => {
  const phone = data?.r1Dealer.tel;
  const email = data?.r1Dealer.email;
  const createdBy = data?.r1Dealer.createdBy;
  const garageName = data?.r1Dealer.garageName;

  return (
    <View style={[styles.col]}>
      {Boolean(garageName) && (
        <View style={styles.line}>
          <TextBold>{garageName}</TextBold>
        </View>
      )}
      {Boolean(createdBy) && (
        <View style={styles.line}>
          <Text>
            <Trans i18nKey={'estimate.pdf.created_by'}>Created by: {{ createdBy }}</Trans>
          </Text>
        </View>
      )}
      {Boolean(phone) && (
        <View style={styles.line}>
          <Text>{phone}</Text>
        </View>
      )}
      {Boolean(email) && (
        <View style={styles.line}>
          <Text>{email}</Text>
        </View>
      )}
    </View>
  );
};

const ObservationCard = ({ data }: { data: EstimatePdfData }) => (
  <View style={[components.border_grey, { width: 180 }]} wrap={false}>
    <View style={[components.card_title_center]}>
      <TextBold>
        <Trans i18nKey="estimate.pdf.observation">Observation</Trans>
      </TextBold>
    </View>
    <View style={[styles.col, { width: '100%', height: '100%' }]}>
      <View style={[styles.flex]}>
        <View style={[styles.col, { marginLeft: 5, marginRight: 5, marginTop: 10, marginBottom: 10 }]}>
          {Boolean(data?.observation) && <Text>{data?.observation}</Text>}
        </View>
      </View>
    </View>
  </View>
);

const AgreementCard = () => {
  return (
    <View style={[components.border_grey, { width: 179 }]} wrap={false}>
      <View style={[components.card_title_center]}>
        <TextBold>
          <Trans i18nKey="estimate.pdf.approved_for_agreement">Approved for Agreement</Trans>
        </TextBold>
      </View>
      <View style={[styles.col, { width: '100%', height: '100%' }]}></View>
    </View>
  );
};

const EstimateTotalCard = ({ data, pdfFilter }: { data: EstimatePdfData; pdfFilter: PdfFilterProps }) => {
  const { currency, price, numberOfItems } = data;
  const { hideDiscounts } = pdfFilter;

  return (
    <View style={[components.border_grey, { width: 180 }]} wrap={false}>
      <View style={[components.card_title_center]}>
        <TextBold>
          <Trans i18nKey="estimate.pdf.estimate_total_items">Estimate total: {{ numberOfItems }} items</Trans>
        </TextBold>
      </View>
      <View style={[styles.col, { width: '100%', height: '100%' }]}>
        <View style={[styles.flex]}>
          <View style={[styles.col, { marginLeft: 5, marginRight: 5, marginTop: 5, marginBottom: 5 }]}>
            <View style={styles.row}>
              <View style={[styles.column, { minWidth: 80 }]}>
                <View style={styles.col}>
                  <TextDark>{<Trans i18nKey="estimate.pdf.total_vat_excl">Total VAT excl. :</Trans>}</TextDark>
                </View>
              </View>
              <View style={[styles.column, { minWidth: 30, maxWidth: 50, alignItems: 'flex-end' }]}>
                <View style={styles.col}>
                  <TextDark>{textFormatter.formatCurrency(Number(price.priceVatExcluded), currency)}</TextDark>
                </View>
              </View>
            </View>
            <Spacer height={5} />
            <View style={styles.row}>
              <View style={[styles.column, { minWidth: 80 }]}>
                <View style={styles.col}>
                  <TextDark>{<Trans i18nKey="estimate.pdf.vat_value">VAT: </Trans>}</TextDark>
                </View>
              </View>
              <View style={[styles.column, { minWidth: 30, maxWidth: 50, alignItems: 'flex-end' }]}>
                <View style={styles.col}>
                  <TextDark>{textFormatter.formatCurrency(Number(price.vat), currency)}</TextDark>
                </View>
              </View>
            </View>
            {!hideDiscounts && Number(price.discount) > 0 && (
              <>
                <Spacer height={5} />
                <View style={styles.row}>
                  <View style={[styles.column, { minWidth: 80 }]}>
                    <View style={styles.col}>
                      <TextDark>{<Trans i18nKey="estimate.pdf.discount">Discount</Trans>}: </TextDark>
                    </View>
                  </View>
                  <View style={[styles.column, { minWidth: 30, maxWidth: 50, alignItems: 'flex-end' }]}>
                    <View style={styles.col}>
                      <TextDark>{textFormatter.formatCurrency(Number(price.discount), currency)}</TextDark>
                    </View>
                  </View>
                </View>
              </>
            )}
            <Spacer />
            <View style={styles.row}>
              <View style={[styles.column, { minWidth: 80 }]}>
                <View style={styles.col}>
                  <TextDark>{<Trans i18nKey="estimate.pdf.total_vat_incl">TOTAL VAT incl. :</Trans>}</TextDark>
                </View>
              </View>
              <View style={[styles.column, { minWidth: 30, maxWidth: 50, alignItems: 'flex-end' }]}>
                <View style={styles.col}>
                  <TextDark>{textFormatter.formatCurrency(Number(price.priceVatIncluded), currency)}</TextDark>
                </View>
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

export const EstimatePage = ({
  data,
  pdfFilter,
  brand,
  isB2B,
}: {
  data: EstimatePdfData;
  pdfFilter: PdfFilterProps;
  // Retained for caller compatibility; the header now renders a brand-based logo placeholder instead of the uploaded logo.
  brand: EstimatePdfBrand;
  isB2B: boolean;
}) => {
  const { hideReferenceNumbers, hideDiscounts } = pdfFilter;
  const eprelReferences = data.reference.filter((ref) => Boolean(ref.eprel));
  const eprelIndexByItemId = new Map(eprelReferences.map((ref, index) => [ref.itemId, index + 1]));
  // References are non-labor lines → Time always shows a dash (NBR4).
  const getReferencesColumns = () =>
    buildUniformColumns<Reference>({
      hideReferenceNumbers,
      hideDiscounts,
      columns: {
        code: {
          title: data.text.code,
          render: (item) => (
            <Text>{textFormatter.formatTextOverflow({ src: item.referenceNumber, maxWordLength: 23 })}</Text>
          ),
        },
        designation: {
          title: data.text.designation,
          render: (item) => {
            const designation = hideReferenceNumbers
              ? removeReferenceFromDesignation(item.designation, item.referenceNumber)
              : item.designation;

            const labelWithMKTPSellerName = item.sellerName ? `${designation} [${item.sellerName}]` : designation;
            const eprelIndex = eprelIndexByItemId.get(item.itemId);
            const labelWithEprelIndex = eprelIndex
              ? `${labelWithMKTPSellerName} (${eprelIndex})`
              : labelWithMKTPSellerName;
            return <Text>{textFormatter.formatTextOverflow({ src: labelWithEprelIndex, maxWordLength: 23 })}</Text>;
          },
        },
        unitPriceRate: {
          title: data.text.unit_price_rate,
          render: (item) => {
            const value = hideDiscounts
              ? Number(item.priceVatExcluded) / Number(item.quantity)
              : Number(item.unitPrice);
            return <Text>{item.unitPrice ? textFormatter.formatCurrency(value, data.currency) : ''}</Text>;
          },
        },
        time: {
          title: <Trans i18nKey="estimate.pdf.time">Time</Trans>,
          render: () => <Text>{PDF_DASH}</Text>,
        },
        quantity: {
          title: data.text.quantity,
          render: (item) => <Text>{item.quantity ? textFormatter.formatNumber(Number(item.quantity)) : ''}</Text>,
        },
        discount: {
          title: data.text.discount,
          render: (item) => (
            <Text>
              {item.discount
                ? item.discountType === 'VALUE_TO_EXTRACT'
                  ? textFormatter.formatCurrency(item.discount, data.currency)
                  : textFormatter.formatPercentDecimal(Number(item.discount))
                : ''}
            </Text>
          ),
        },
        priceExclVat: {
          title: data.text.price_vat_excl,
          render: (item) => (
            <TextBold>
              {item.priceVatExcluded ? textFormatter.formatCurrency(item.priceVatExcluded, data.currency) : ''}
            </TextBold>
          ),
        },
        vat: {
          title: data.text.vat,
          render: (item) => (
            <Text>
              {item.vatPercentage !== undefined && item.vatPercentage >= 0
                ? textFormatter.formatPercentDecimal(Number(item.vatPercentage))
                : ''}
            </Text>
          ),
        },
      },
    });

  // Labor lines show their duration in the Time column and the hourly rate in Unit Price - Rate (NBR4).
  const getLaborTimeColumns = () =>
    buildUniformColumns<LaborTime>({
      hideReferenceNumbers,
      hideDiscounts,
      columns: {
        code: {
          title: data.text.code,
          render: (item) => <Text>{textFormatter.formatTextOverflow({ src: item.code, maxWordLength: 20 })}</Text>,
        },
        designation: {
          title: data.text.designation,
          render: (item) => (
            <Text>{textFormatter.formatTextOverflow({ src: item.designation, maxWordLength: 20 })}</Text>
          ),
        },
        unitPriceRate: {
          title: data.text.unit_price_rate,
          render: (item) => {
            const value = !hideDiscounts
              ? item.pricePerHour
              : Number(item.priceVatExcluded) / Number(item.numberOfHours) / Number(item.quantity);
            return <Text>{item.pricePerHour ? textFormatter.formatCurrency(value, data.currency) : ''}</Text>;
          },
        },
        time: {
          title: <Trans i18nKey="estimate.pdf.time">Time</Trans>,
          render: (item) => (
            <Text>
              {item.numberOfHours ? textFormatter.formatAmount(Number(item.numberOfHours), 'hour', 'short') : ''}
            </Text>
          ),
        },
        quantity: {
          title: data.text.quantity,
          render: (item) => <Text>{item.quantity ? textFormatter.formatNumber(Number(item.quantity)) : ''}</Text>,
        },
        discount: {
          title: data.text.discount,
          render: (item) => (
            <Text>
              {item.discount
                ? item.discountType === 'VALUE_TO_EXTRACT'
                  ? textFormatter.formatCurrency(item.discount, data.currency)
                  : textFormatter.formatPercentDecimal(Number(item.discount))
                : ''}
            </Text>
          ),
        },
        priceExclVat: {
          title: data.text.price_vat_excl,
          render: (item) => (
            <TextBold>
              {item.priceVatExcluded ? textFormatter.formatCurrency(item.priceVatExcluded, data.currency) : ''}
            </TextBold>
          ),
        },
        vat: {
          title: data.text.vat,
          render: (item) => (
            <Text>
              {item.vatPercentage !== undefined && item.vatPercentage >= 0
                ? textFormatter.formatPercentDecimal(Number(item.vatPercentage))
                : ''}
            </Text>
          ),
        },
      },
    });

  // Bundles / waste-recycling are non-labor lines → Time always shows a dash (NBR4).
  const getBundleOrRecyclingColumns = () =>
    buildUniformColumns<BundleOrRecycling>({
      hideReferenceNumbers,
      hideDiscounts,
      columns: {
        code: {
          title: data.text.code,
          render: (item) => (
            <Text>{item.code ? textFormatter.formatTextOverflow({ src: item.code, maxWordLength: 20 }) : ''}</Text>
          ),
        },
        designation: {
          title: data.text.designation,
          render: (item) => <Text>{textFormatter.formatTextOverflow({ src: item.name, maxWordLength: 23 })}</Text>,
        },
        unitPriceRate: {
          title: data.text.unit_price_rate,
          render: (item) => {
            const value = hideDiscounts
              ? Number(item.priceVatExcluded) / Number(item.quantity)
              : Number(item.unitPrice);
            return <Text>{item.unitPrice ? textFormatter.formatCurrency(value, data.currency) : ''}</Text>;
          },
        },
        time: {
          title: <Trans i18nKey="estimate.pdf.time">Time</Trans>,
          render: () => <Text>{PDF_DASH}</Text>,
        },
        quantity: {
          title: data.text.quantity,
          render: (item) => <Text>{item.quantity ? textFormatter.formatNumber(Number(item.quantity)) : ''}</Text>,
        },
        discount: {
          title: data.text.discount,
          render: (item) => (
            <Text>
              {item.discount
                ? item.discountType === 'VALUE_TO_EXTRACT'
                  ? textFormatter.formatCurrency(item.discount, data.currency)
                  : textFormatter.formatPercentDecimal(Number(item.discount))
                : ''}
            </Text>
          ),
        },
        priceExclVat: {
          title: data.text.price_vat_excl,
          render: (item) => (
            <TextBold>
              {item.priceVatExcluded ? textFormatter.formatCurrency(item.priceVatExcluded, data.currency) : ''}
            </TextBold>
          ),
        },
        vat: {
          title: data.text.vat,
          render: (item) => (
            <Text>
              {item.vatPercentage !== undefined && item.vatPercentage >= 0
                ? textFormatter.formatPercentDecimal(Number(item.vatPercentage))
                : ''}
            </Text>
          ),
        },
      },
    });

  const { t } = useTranslation();
  const renderTableHeader = (title: string): ReactNode => (
    <View style={[components.card_title_center, { height: '19px' }]}>
      <TextBold>{title}</TextBold>
    </View>
  );
  const tableStyles = {
    headerCell: { backgroundColor: '#978B7F33', ...fonts.text_bold, fontFamily: 'NouvelR', fontWeight: 600 },
    wrapper: {
      borderRight: `1px solid ${theme.color.grey95}`,
    },
    row: {
      borderBottom: `1px solid ${theme.color.grey95}`,
    },
    cell: {
      borderLeft: `1px solid ${theme.color.grey95}`,
      minHeight: '19px',
      paddingLeft: '4px',
      paddingRight: '4px',
      justifyContent: 'flex-start',
    } as const,
  };
  return (
    <Document>
      <Page size={'A4'} style={styles.estimate_page}>
        <PageNumber />
        <PdfHeaderBloc
          topLeft={
            <>
              {isB2B ? (
                <>
                  <Image src={'/images/estimate/b2b_logo.png'} style={{ width: 65, height: 65 }} />
                  <VerticalSpacer width={7} />
                  <R1GarageCard data={data} />
                </>
              ) : (
                <GarageCard data={data} />
              )}
            </>
          }
          topRight={
            <>
              {!isB2B && (
                <>
                  <View style={styles.row}>{getBrandLogo(brand)}</View>
                  <Spacer height={5} />
                </>
              )}
              <ClientContactCard data={data} isB2B={isB2B} />
            </>
          }
        />
        <Spacer height={8} />
        <PdfTwoPanelBlock
          leftPanel={<EstimateInfoPanel data={data} width={data.vehicleDetail ? 200 : 563} />}
          rightPanel={data.vehicleDetail ? <VehicleCard data={data} width={353} /> : undefined}
        />
        <Spacer height={8} />
        {
          <Table
            data={[]}
            columns={getReferencesColumns()}
            maxWidth={563}
            tableStyles={tableStyles}
            boldDefaultHeader
          />
        }
        {data.reference.length > 0 && (
          <Table
            data={data.reference as Reference[]}
            columns={getReferencesColumns()}
            customHeader={renderTableHeader(t('estimate.pdf.references', 'References'))}
            maxWidth={563}
            tableStyles={tableStyles}
          />
        )}
        {data.laborTime.length > 0 && !isB2B && (
          <Table
            data={data.laborTime}
            columns={getLaborTimeColumns()}
            customHeader={renderTableHeader(t('estimate.pdf.labor_times', 'Labor times'))}
            maxWidth={563}
            tableStyles={tableStyles}
          />
        )}
        {data.bundlesOrRecycling.length > 0 && !isB2B && (
          <Table
            data={data.bundlesOrRecycling}
            columns={getBundleOrRecyclingColumns()}
            customHeader={renderTableHeader(t('estimate.pdf.bundles', 'Bundles'))}
            maxWidth={563}
            tableStyles={tableStyles}
          />
        )}
        {data.operations.length > 0 &&
          data.operations.map((op) => {
            return (
              <>
                <Table
                  data={op.referenceSubsection.references}
                  columns={getReferencesColumns()}
                  customHeader={renderTableHeader(op.name)}
                  maxWidth={563}
                  tableStyles={tableStyles}
                />
                <Divider />
                <Divider />
                <Table
                  data={op.laborTimeSubsection.laborTimes}
                  columns={getLaborTimeColumns()}
                  customHeader={<></>}
                  maxWidth={563}
                  tableStyles={tableStyles}
                />
              </>
            );
          })}
        <Spacer height={12} />
        <View style={[styles.row, { height: 100 }]}>
          <ObservationCard data={data} />
          <VerticalSpacer width={12} />
          <AgreementCard />
          <VerticalSpacer width={12} />
          <EstimateTotalCard data={data} pdfFilter={pdfFilter} />
        </View>
        {eprelReferences.length > 0 && (
          <View wrap={false}>
            <Spacer height={8} />
            <Subtitle>{data.text.eprel}</Subtitle>
            <Spacer height={8} />
            <View style={[styles.row, { flexWrap: 'wrap' }]}>
              {eprelReferences.map((item, index) => (
                <View key={item.itemId} style={[styles.col, { alignItems: 'center', marginRight: 10 }]}>
                  <Text>{index + 1}</Text>
                  {item.eprel && <EprelPicture eprelImageUri={item.eprel} />}
                </View>
              ))}
            </View>
          </View>
        )}
        <Spacer height={12} />
        {Boolean(data.legalMention) && (
          <View>
            <Subtitle>
              <Trans i18nKey="estimate.pdf.legal_mention">Legal mention</Trans>
            </Subtitle>
            <Spacer />
            <Text>{data.legalMention}</Text>
          </View>
        )}
      </Page>
    </Document>
  );
};
